import { inject } from '@angular/core';
import { RedirectCommand, ResolveFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { map, race, timer } from 'rxjs';

import { CheckoutFacade } from 'ish-core/facades/checkout.facade';
import { loadBasket } from 'ish-core/store/customer/basket';
import { whenTruthy } from 'ish-core/utils/operators';

/**
 * Ensures a basket is loaded before the route activates.
 *
 * Falls back to redirecting to '/basket' if no basket becomes available within 4s (e.g. an
 * expired/404 basket after returning from an external payment redirect) instead of leaving the
 * route resolution pending forever, mirroring checkoutPageGuard's timeout pattern.
 * A resolver only redirects on a RedirectCommand, not a plain UrlTree (that only applies to
 * guards) - see Angular's `resolveNode` in `@angular/router`.
 */
export const basketResolver: ResolveFn<unknown> = () => {
  const checkoutFacade = inject(CheckoutFacade);
  const store = inject(Store);
  const router = inject(Router);

  // Dispatch basket load if not already loaded
  store.dispatch(loadBasket());

  // Wait until basket is available (non-null), or redirect to the basket page if it never arrives
  return race(
    checkoutFacade.basket$.pipe(whenTruthy()),
    timer(4000).pipe(map(() => new RedirectCommand(router.parseUrl('/basket'))))
  );
};
